import { Injectable } from "@angular/core";
import { MainState } from "@app/store/state";
import { ContributorBadge } from "@core/enums/forum/contributor-badge.enum";
import { PostInterface } from "@core/interfaces/forums/post.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { ForumApiService } from "@core/services/api/forum/forum-api.service";
import { LoadingService } from "@core/services/loading.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, map, mergeMap, tap } from "rxjs/operators";

import {
  ApprovePost,
  ApprovePostFailure,
  ApprovePostSuccess,
  CancelPollVote,
  CancelPollVoteFailure,
  CancelPollVoteSuccess,
  CreatePost,
  CreatePostFailure,
  CreatePostSuccess,
  DeletePost,
  DeletePostFailure,
  DeletePostSuccess,
  ForumActionTypes,
  GetCategory,
  GetCategoryFailure,
  GetCategorySuccess,
  GetForum,
  GetForumFailure,
  GetForumSuccess,
  GetPost,
  GetPostFailure,
  GetPostSuccess,
  GetTopic,
  GetTopicFailure,
  GetTopicSuccess,
  LoadCategoriesFailure,
  LoadCategoriesSuccess,
  LoadContributorBadges,
  LoadContributorBadgesFailure,
  LoadContributorBadgesSuccess,
  LoadForums,
  LoadForumsFailure,
  LoadForumsSuccess,
  LoadPosts,
  LoadPostsFailure,
  LoadPostsSuccess,
  LoadTags,
  LoadTagsFailure,
  LoadTagsSuccess,
  LoadTopicListBadges,
  LoadTopicListBadgesFailure,
  LoadTopicListBadgesSuccess,
  LoadPostBadges,
  LoadPostBadgesFailure,
  LoadPostBadgesSuccess,
  LoadPostLikes,
  LoadPostLikesSuccess,
  LoadPostLikesFailure,
  LoadTopics,
  LoadTopicsFailure,
  LoadTopicsSuccess,
  MarkPostAsRead,
  MarkPostAsReadFailure,
  MarkPostAsReadSuccess,
  SubscribeTopic,
  SubscribeTopicFailure,
  SubscribeTopicSuccess,
  UnsubscribeTopic,
  UnsubscribeTopicFailure,
  UnsubscribeTopicSuccess,
  VotePoll,
  VotePollFailure,
  VotePollSuccess,
  EditTocEntry,
  EditTocEntrySuccess,
  EditTocEntryFailure,
  UpdateTopicTags,
  UpdateTopicTagsSuccess,
  UpdateTopicTagsFailure,
  LoadCommunityNotes,
  LoadCommunityNotesSuccess,
  LoadCommunityNotesFailure,
  CreateCommunityNote,
  CreateCommunityNoteSuccess,
  CreateCommunityNoteFailure,
  UpdateCommunityNote,
  UpdateCommunityNoteSuccess,
  UpdateCommunityNoteFailure,
  DeleteCommunityNote,
  DeleteCommunityNoteSuccess,
  DeleteCommunityNoteFailure,
  UpvoteCommunityNote,
  UpvoteCommunityNoteSuccess,
  UpvoteCommunityNoteFailure,
  RemoveUpvoteCommunityNote,
  RemoveUpvoteCommunityNoteSuccess,
  RemoveUpvoteCommunityNoteFailure,
  LoadCommunityNoteHistory,
  LoadCommunityNoteHistorySuccess,
  LoadCommunityNoteHistoryFailure,
  CloseTopic,
  CloseTopicSuccess,
  CloseTopicFailure,
  ReopenTopic,
  ReopenTopicSuccess,
  ReopenTopicFailure,
  MakeStickyTopic,
  MakeStickyTopicSuccess,
  MakeStickyTopicFailure,
  RemoveStickyTopic,
  RemoveStickyTopicSuccess,
  RemoveStickyTopicFailure,
  MoveTopic,
  MoveTopicSuccess,
  MoveTopicFailure
} from "./forum.actions";
import { ForumTocService } from "../services/forum-toc.service";

@Injectable()
export class ForumEffects {
  // Categories
  LoadCategories: Observable<LoadCategoriesSuccess | LoadCategoriesFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_CATEGORIES),
      mergeMap(() => {
        this.loadingService.setLoading(true);
        return this.forumApiService.getCategories().pipe(
          map(
            response =>
              new LoadCategoriesSuccess({
                categories: response.results,
                total: response.count
              })
          ),
          catchError(error => of(new LoadCategoriesFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Forums
  LoadForums: Observable<LoadForumsSuccess | LoadForumsFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_FORUMS),
      map((action: LoadForums) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.loadForums(payload.categoryId, payload.parentForumId, payload.page || 1).pipe(
          map(
            response =>
              new LoadForumsSuccess({
                forums: response.results,
                total: response.count
              })
          ),
          catchError(error => of(new LoadForumsFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Topics
  LoadTopics: Observable<LoadTopicsSuccess | LoadTopicsFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_TOPICS),
      map((action: LoadTopics) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.loadTopics(payload.forumId, payload.page || 1).pipe(
          map(
            response =>
              new LoadTopicsSuccess({
                topics: response.results,
                total: response.count,
                hasNext: !!response.next
              })
          ),
          catchError(error => of(new LoadTopicsFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Posts - will need to be implemented in the API service
  LoadPosts: Observable<LoadPostsSuccess | LoadPostsFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_POSTS),
      map((action: LoadPosts) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        // Note: we need to implement this in the ForumApiService
        return this.forumApiService.loadPosts(payload.topicId, payload.page || 1).pipe(
          map(
            response =>
              new LoadPostsSuccess({
                posts: response.results,
                total: response.count,
                hasNext: !!response.next
              })
          ),
          catchError(error => of(new LoadPostsFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Single item getters
  GetCategory: Observable<GetCategorySuccess | GetCategoryFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.GET_CATEGORY),
      map((action: GetCategory) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.getCategory(payload.categoryId).pipe(
          map(category => new GetCategorySuccess({ category })),
          catchError(error => of(new GetCategoryFailure({ error: error.statusText })))
        );
      })
    )
  );

  GetForum: Observable<GetForumSuccess | GetForumFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.GET_FORUM),
      map((action: GetForum) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.getForum(payload.forumId).pipe(
          map(forum => new GetForumSuccess({ forum })),
          catchError(error => of(new GetForumFailure({ error: error.statusText })))
        );
      })
    )
  );

  GetTopic: Observable<GetTopicSuccess | GetTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.GET_TOPIC),
      map((action: GetTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        // Note: we need to implement this in the ForumApiService
        return this.forumApiService.getTopic(payload.topicId).pipe(
          map(topic => new GetTopicSuccess({ topic })),
          catchError(error => of(new GetTopicFailure({ error: error.statusText })))
        );
      })
    )
  );

  GetPost: Observable<GetPostSuccess | GetPostFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.GET_POST),
      map((action: GetPost) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        // Note: we need to implement this in the ForumApiService
        return this.forumApiService.getPost(payload.postId).pipe(
          map(post => new GetPostSuccess({ post })),
          catchError(error => of(new GetPostFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Contributor Badges
  LoadContributorBadges: Observable<LoadContributorBadgesSuccess | LoadContributorBadgesFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_CONTRIBUTOR_BADGES),
      map((action: LoadContributorBadges) => action.payload),
      mergeMap(payload => {
        return this.forumApiService.getContributorBadges(payload.topicId).pipe(
          map(rawBadges => {
            // Convert string array to ContributorBadge[] type
            const badges: Record<string, ContributorBadge[]> = {};

            Object.entries(rawBadges).forEach(([userId, badgeStrings]) => {
              badges[userId] = badgeStrings.map(badgeStr => badgeStr as unknown as ContributorBadge);
            });

            return new LoadContributorBadgesSuccess({ topicId: payload.topicId, badges });
          }),
          catchError(error => of(new LoadContributorBadgesFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Topic List Badges
  LoadTopicListBadges: Observable<LoadTopicListBadgesSuccess | LoadTopicListBadgesFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_TOPIC_LIST_BADGES),
      map((action: LoadTopicListBadges) => action.payload),
      mergeMap(payload => {
        return this.forumApiService.getMultipleTopicBadges(payload.topicIds).pipe(
          map(response => {
            // Convert the response to the expected format
            const processedResponse: Record<string, { userId: string; badges: ContributorBadge[] }> = {};

            // Process the API response
            Object.entries(response).forEach(([topicId, data]) => {
              processedResponse[topicId] = {
                userId: data.userId,
                badges: data.badges.map(badgeStr => badgeStr as unknown as ContributorBadge)
              };
            });

            return new LoadTopicListBadgesSuccess({ badges: processedResponse });
          }),
          catchError(error => of(new LoadTopicListBadgesFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Post Likes
  LoadPostLikes: Observable<LoadPostLikesSuccess | LoadPostLikesFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_POST_LIKES),
      map((action: LoadPostLikes) => action.payload),
      mergeMap(payload => {
        return this.commonApiService
          .getAllTogglePropertiesForObjects("like", payload.contentType, payload.postIds)
          .pipe(
            map(toggleProperties => {
              // Count likes per post
              const likeCounts: Record<PostInterface["id"], number> = {};
              const userLikedPosts: PostInterface["id"][] = [];

              // Initialize all posts with 0 likes
              payload.postIds.forEach(postId => {
                likeCounts[postId] = 0;
              });

              // Count likes for each post and track current user's likes
              toggleProperties.forEach(prop => {
                if (likeCounts[prop.objectId] !== undefined) {
                  likeCounts[prop.objectId]++;

                  // Check if this like is from the current user
                  if (payload.currentUserId && prop.user === payload.currentUserId) {
                    userLikedPosts.push(prop.objectId);
                  }
                }
              });

              return new LoadPostLikesSuccess({ likeCounts, userLikedPosts });
            }),
            catchError(error =>
              of(new LoadPostLikesFailure({ error: error.statusText || "Failed to load post likes" }))
            )
          );
      })
    )
  );

  // Post Badges
  LoadPostBadges: Observable<LoadPostBadgesSuccess | LoadPostBadgesFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_POST_BADGES),
      map((action: LoadPostBadges) => action.payload),
      mergeMap(payload => {
        return this.forumApiService.getPostBadges(payload.topicId).pipe(
          map(response => {
            return new LoadPostBadgesSuccess({ topicId: payload.topicId, badges: response });
          }),
          catchError(error => of(new LoadPostBadgesFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Topic Subscription
  SubscribeTopic: Observable<SubscribeTopicSuccess | SubscribeTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.SUBSCRIBE_TOPIC),
      map((action: SubscribeTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.subscribeTopic(payload.topicId).pipe(
          map(() => new SubscribeTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new SubscribeTopicFailure({ error: error.statusText })))
        );
      })
    )
  );

  UnsubscribeTopic: Observable<UnsubscribeTopicSuccess | UnsubscribeTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.UNSUBSCRIBE_TOPIC),
      map((action: UnsubscribeTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.unsubscribeTopic(payload.topicId).pipe(
          map(() => new UnsubscribeTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new UnsubscribeTopicFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Poll Voting
  VotePoll: Observable<VotePollSuccess | VotePollFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.VOTE_POLL),
      map((action: VotePoll) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.votePoll(payload.topicId, payload.pollType, payload.answerIds).pipe(
          map(topic => new VotePollSuccess({ topic })),
          catchError(error => of(new VotePollFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Cancel Poll Vote
  CancelPollVote: Observable<CancelPollVoteSuccess | CancelPollVoteFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.CANCEL_POLL_VOTE),
      map((action: CancelPollVote) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.cancelPollVote(payload.topicId).pipe(
          map(topic => new CancelPollVoteSuccess({ topic })),
          catchError(error => of(new CancelPollVoteFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Create Post
  CreatePost: Observable<CreatePostSuccess | CreatePostFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.CREATE_POST),
      map((action: CreatePost) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.createPost(payload).pipe(
          tap(() => this.loadingService.setLoading(false)),
          map(post => new CreatePostSuccess({ post })),
          catchError(error => {
            this.loadingService.setLoading(false);
            return of(new CreatePostFailure({ error }));
          })
        );
      })
    )
  );

  // Delete Post
  DeletePost: Observable<DeletePostSuccess | DeletePostFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.DELETE_POST),
      map((action: DeletePost) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.deletePost(payload.postId).pipe(
          map(() => new DeletePostSuccess({ postId: payload.postId })),
          catchError(error => of(new DeletePostFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Mark Post as Read
  MarkPostAsRead: Observable<MarkPostAsReadSuccess | MarkPostAsReadFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.MARK_POST_AS_READ),
      map((action: MarkPostAsRead) => action.payload),
      mergeMap(payload => {
        return this.forumApiService.markPostAsRead(payload.topicId, payload.postId).pipe(
          map(topic => new MarkPostAsReadSuccess({ topic })),
          catchError(error => of(new MarkPostAsReadFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Approve Post
  ApprovePost: Observable<ApprovePostSuccess | ApprovePostFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.APPROVE_POST),
      map((action: ApprovePost) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.approvePost(payload.postId).pipe(
          map(post => new ApprovePostSuccess({ post })),
          catchError(error => of(new ApprovePostFailure({ error: error.statusText })))
        );
      })
    )
  );

  // Load Tags
  LoadTags: Observable<LoadTagsSuccess | LoadTagsFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_TAGS),
      map((action: LoadTags) => action.payload),
      mergeMap(payload => {
        return this.forumApiService
          .getTags(
            payload.page || 1,
            payload.search,
            payload.followed,
            payload.ordering,
            payload.excludeCategories,
            payload.category,
            payload.pageSize
          )
          .pipe(
            map(
              response =>
                new LoadTagsSuccess({
                  tags: response.results,
                  total: response.count,
                  append: payload.append
                })
            ),
            catchError(error => of(new LoadTagsFailure({ error: error.statusText })))
          );
      })
    )
  );

  // Table of Contents Edit
  EditTocEntry = createEffect(() =>
    this.actions$.pipe(
      ofType<EditTocEntry>(ForumActionTypes.EDIT_TOC_ENTRY),
      mergeMap(action =>
        this.forumTocService
          .editTocEntry(
            action.payload.topicId,
            action.payload.entryId,
            action.payload.summary,
            action.payload.editReason
          )
          .pipe(
            map(
              updatedEntry =>
                new EditTocEntrySuccess({
                  topicId: action.payload.topicId,
                  entryId: action.payload.entryId,
                  updatedEntry
                })
            ),
            catchError(error => of(new EditTocEntryFailure({ error })))
          )
      )
    )
  );

  // Update topic tags
  UpdateTopicTags = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateTopicTags>(ForumActionTypes.UPDATE_TOPIC_TAGS),
      mergeMap(action => {
        this.loadingService.setLoading(true);
        return this.forumApiService.updateTopicTags(action.payload.topicId, action.payload.tagSlugs).pipe(
          map(topic => new UpdateTopicTagsSuccess({ topic })),
          catchError(error => of(new UpdateTopicTagsFailure({ error })))
        );
      })
    )
  );

  // Community Notes effects
  loadCommunityNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_COMMUNITY_NOTES),
      mergeMap((action: LoadCommunityNotes) =>
        this.forumApiService.getCommunityNotes(action.payload.postId).pipe(
          map(
            response =>
              new LoadCommunityNotesSuccess({
                postId: action.payload.postId,
                notes: response.results || []
              })
          ),
          catchError(error =>
            of(
              new LoadCommunityNotesFailure({
                postId: action.payload.postId,
                error: error.message || "Failed to load community notes"
              })
            )
          )
        )
      )
    )
  );

  createCommunityNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.CREATE_COMMUNITY_NOTE),
      mergeMap((action: CreateCommunityNote) => {
        this.loadingService.setLoading(true);
        return this.forumApiService.createCommunityNote(action.payload.postId, action.payload.note).pipe(
          map(note => new CreateCommunityNoteSuccess({ note })),
          catchError(error =>
            of(
              new CreateCommunityNoteFailure({ error: error.error?.detail || error.message || "Failed to create note" })
            )
          )
        );
      })
    )
  );

  updateCommunityNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.UPDATE_COMMUNITY_NOTE),
      mergeMap((action: UpdateCommunityNote) => {
        this.loadingService.setLoading(true);
        return this.forumApiService.updateCommunityNote(action.payload.noteId, action.payload.updates).pipe(
          map(note => new UpdateCommunityNoteSuccess({ note })),
          catchError(error => of(new UpdateCommunityNoteFailure({ error: error.message || "Failed to update note" })))
        );
      })
    )
  );

  deleteCommunityNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.DELETE_COMMUNITY_NOTE),
      mergeMap((action: DeleteCommunityNote) => {
        this.loadingService.setLoading(true);
        return this.forumApiService.deleteCommunityNote(action.payload.noteId).pipe(
          map(() => new DeleteCommunityNoteSuccess({ noteId: action.payload.noteId })),
          catchError(error => of(new DeleteCommunityNoteFailure({ error: error.message || "Failed to delete note" })))
        );
      })
    )
  );

  upvoteCommunityNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.UPVOTE_COMMUNITY_NOTE),
      mergeMap((action: UpvoteCommunityNote) =>
        this.forumApiService.upvoteCommunityNote(action.payload.noteId).pipe(
          map(() => new UpvoteCommunityNoteSuccess({ noteId: action.payload.noteId })),
          catchError(error =>
            of(
              new UpvoteCommunityNoteFailure({
                noteId: action.payload.noteId,
                error: error.message || "Failed to upvote note"
              })
            )
          )
        )
      )
    )
  );

  removeUpvoteCommunityNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE),
      mergeMap((action: RemoveUpvoteCommunityNote) =>
        this.forumApiService.removeUpvoteCommunityNote(action.payload.noteId).pipe(
          map(() => new RemoveUpvoteCommunityNoteSuccess({ noteId: action.payload.noteId })),
          catchError(error =>
            of(
              new RemoveUpvoteCommunityNoteFailure({
                noteId: action.payload.noteId,
                error: error.message || "Failed to remove upvote"
              })
            )
          )
        )
      )
    )
  );

  loadCommunityNoteHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY),
      mergeMap((action: LoadCommunityNoteHistory) => {
        this.loadingService.setLoading(true);
        return this.forumApiService.getCommunityNoteHistory(action.payload.noteId).pipe(
          map(history => new LoadCommunityNoteHistorySuccess({ history })),
          catchError(error =>
            of(
              new LoadCommunityNoteHistoryFailure({
                noteId: action.payload.noteId,
                error: error.message || "Failed to load note history"
              })
            )
          )
        );
      })
    )
  );

  // Topic Moderation Effects
  CloseTopic: Observable<CloseTopicSuccess | CloseTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.CLOSE_TOPIC),
      map((action: CloseTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.closeTopic(payload.topicId).pipe(
          map(() => new CloseTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new CloseTopicFailure({ error })))
        );
      })
    )
  );

  ReopenTopic: Observable<ReopenTopicSuccess | ReopenTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.REOPEN_TOPIC),
      map((action: ReopenTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.reopenTopic(payload.topicId).pipe(
          map(() => new ReopenTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new ReopenTopicFailure({ error })))
        );
      })
    )
  );

  MakeStickyTopic: Observable<MakeStickyTopicSuccess | MakeStickyTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.MAKE_STICKY_TOPIC),
      map((action: MakeStickyTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.makeStickyTopic(payload.topicId).pipe(
          map(() => new MakeStickyTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new MakeStickyTopicFailure({ error })))
        );
      })
    )
  );

  RemoveStickyTopic: Observable<RemoveStickyTopicSuccess | RemoveStickyTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.REMOVE_STICKY_TOPIC),
      map((action: RemoveStickyTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.removeStickyTopic(payload.topicId).pipe(
          map(() => new RemoveStickyTopicSuccess({ topicId: payload.topicId })),
          catchError(error => of(new RemoveStickyTopicFailure({ error })))
        );
      })
    )
  );

  MoveTopic: Observable<MoveTopicSuccess | MoveTopicFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ForumActionTypes.MOVE_TOPIC),
      map((action: MoveTopic) => action.payload),
      mergeMap(payload => {
        this.loadingService.setLoading(true);
        return this.forumApiService.moveTopic(payload.topicId, payload.forumId).pipe(
          map(
            response =>
              new MoveTopicSuccess({
                topicId: payload.topicId,
                newForumId: response.new_forum_id,
                newForumName: response.new_forum_name,
                newSlug: response.new_slug
              })
          ),
          catchError(error => of(new MoveTopicFailure({ error })))
        );
      })
    )
  );

  // After successful API calls
  SuccessActions = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ForumActionTypes.LOAD_CATEGORIES_SUCCESS,
          ForumActionTypes.LOAD_FORUMS_SUCCESS,
          ForumActionTypes.LOAD_TOPICS_SUCCESS,
          ForumActionTypes.LOAD_POSTS_SUCCESS,
          ForumActionTypes.LOAD_TAGS_SUCCESS,
          ForumActionTypes.GET_CATEGORY_SUCCESS,
          ForumActionTypes.GET_FORUM_SUCCESS,
          ForumActionTypes.GET_TOPIC_SUCCESS,
          ForumActionTypes.GET_POST_SUCCESS,
          ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_SUCCESS,
          ForumActionTypes.LOAD_TOPIC_LIST_BADGES_SUCCESS,
          ForumActionTypes.SUBSCRIBE_TOPIC_SUCCESS,
          ForumActionTypes.UNSUBSCRIBE_TOPIC_SUCCESS,
          ForumActionTypes.VOTE_POLL_SUCCESS,
          ForumActionTypes.CANCEL_POLL_VOTE_SUCCESS,
          ForumActionTypes.DELETE_POST_SUCCESS,
          ForumActionTypes.APPROVE_POST_SUCCESS,
          ForumActionTypes.MARK_POST_AS_READ_SUCCESS,
          ForumActionTypes.EDIT_TOC_ENTRY_SUCCESS,
          ForumActionTypes.UPDATE_TOPIC_TAGS_SUCCESS,
          ForumActionTypes.CREATE_COMMUNITY_NOTE_SUCCESS,
          ForumActionTypes.UPDATE_COMMUNITY_NOTE_SUCCESS,
          ForumActionTypes.DELETE_COMMUNITY_NOTE_SUCCESS,
          ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_SUCCESS,
          ForumActionTypes.CLOSE_TOPIC_SUCCESS,
          ForumActionTypes.REOPEN_TOPIC_SUCCESS,
          ForumActionTypes.MAKE_STICKY_TOPIC_SUCCESS,
          ForumActionTypes.REMOVE_STICKY_TOPIC_SUCCESS,
          ForumActionTypes.MOVE_TOPIC_SUCCESS
        ),
        tap(() => this.loadingService.setLoading(false))
      ),
    { dispatch: false }
  );

  // After failed API calls
  FailureActions = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ForumActionTypes.LOAD_CATEGORIES_FAILURE,
          ForumActionTypes.LOAD_FORUMS_FAILURE,
          ForumActionTypes.LOAD_TOPICS_FAILURE,
          ForumActionTypes.LOAD_POSTS_FAILURE,
          ForumActionTypes.LOAD_TAGS_FAILURE,
          ForumActionTypes.GET_CATEGORY_FAILURE,
          ForumActionTypes.GET_FORUM_FAILURE,
          ForumActionTypes.GET_TOPIC_FAILURE,
          ForumActionTypes.GET_POST_FAILURE,
          ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_FAILURE,
          ForumActionTypes.LOAD_TOPIC_LIST_BADGES_FAILURE,
          ForumActionTypes.VOTE_POLL_FAILURE,
          ForumActionTypes.CANCEL_POLL_VOTE_FAILURE,
          ForumActionTypes.DELETE_POST_FAILURE,
          ForumActionTypes.APPROVE_POST_FAILURE,
          ForumActionTypes.MARK_POST_AS_READ_FAILURE,
          ForumActionTypes.EDIT_TOC_ENTRY_FAILURE,
          ForumActionTypes.UPDATE_TOPIC_TAGS_FAILURE,
          ForumActionTypes.CREATE_COMMUNITY_NOTE_FAILURE,
          ForumActionTypes.UPDATE_COMMUNITY_NOTE_FAILURE,
          ForumActionTypes.DELETE_COMMUNITY_NOTE_FAILURE,
          ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_FAILURE,
          ForumActionTypes.CLOSE_TOPIC_FAILURE,
          ForumActionTypes.REOPEN_TOPIC_FAILURE,
          ForumActionTypes.MAKE_STICKY_TOPIC_FAILURE,
          ForumActionTypes.REMOVE_STICKY_TOPIC_FAILURE,
          ForumActionTypes.MOVE_TOPIC_FAILURE
        ),
        tap(() => this.loadingService.setLoading(false))
      ),
    { dispatch: false }
  );

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly forumApiService: ForumApiService,
    public readonly commonApiService: CommonApiService,
    public readonly loadingService: LoadingService,
    public readonly forumTocService: ForumTocService
  ) {}
}
